import { useEffect, useMemo, useState, type ReactNode, type RefObject } from "react"

export const GRAPH_HEIGHT_PX = 35
const REFERENCE_COUNT = 1000
const LOG_REFERENCE = Math.log1p(REFERENCE_COUNT)
const CURVE_SMOOTHING = 0.45
const CHUNK_BUCKETS = 48
const CHUNK_MARGIN = 1

const columnY = (count: number) => {
    const normalized = Math.min(1, Math.log1p(count) / LOG_REFERENCE)
    return GRAPH_HEIGHT_PX - normalized * GRAPH_HEIGHT_PX
}

const monotoneTangents = (ys: Float32Array, dx: number) => {
    const n = ys.length
    const slopes = new Float32Array(n - 1)
    for (let i = 0; i < n - 1; i++) slopes[i] = (ys[i + 1] - ys[i]) / dx
    const tangents = new Float32Array(n)
    for (let i = 0; i < n; i++) {
        if (i === 0) tangents[i] = slopes[0]
        else if (i === n - 1) tangents[i] = slopes[n - 2]
        else tangents[i] = slopes[i - 1] * slopes[i] <= 0 ? 0 : (slopes[i - 1] + slopes[i]) / 2
    }
    for (let i = 0; i < n - 1; i++) {
        if (slopes[i] === 0) {
            tangents[i] = 0
            tangents[i + 1] = 0
            continue
        }
        const a = tangents[i] / slopes[i]
        const b = tangents[i + 1] / slopes[i]
        const magnitude = a * a + b * b
        if (magnitude > 9) {
            const scale = 3 / Math.sqrt(magnitude)
            tangents[i] = scale * a * slopes[i]
            tangents[i + 1] = scale * b * slopes[i]
        }
    }
    return tangents
}

const round = (n: number) => Math.round(n * 10) / 10

const chunkPath = (
    ys: Float32Array,
    tangents: Float32Array,
    from: number,
    to: number,
    x0: number,
    dx: number
) => {
    const startX = x0 + from * dx
    const parts = [`M${round(startX)},${GRAPH_HEIGHT_PX}`, `L${round(startX)},${round(ys[from])}`]
    for (let i = from; i < to; i++) {
        const x = x0 + i * dx
        const chord = (ys[i + 1] - ys[i]) / dx
        const lead = chord + (tangents[i] - chord) * CURVE_SMOOTHING
        const trail = chord + (tangents[i + 1] - chord) * CURVE_SMOOTHING
        const c1y = ys[i] + (lead * dx) / 3
        const c2y = ys[i + 1] - (trail * dx) / 3
        parts.push(
            `C${round(x + dx / 3)},${round(c1y)} ${round(x + (2 * dx) / 3)},${round(c2y)} ${round(x + dx)},${round(ys[i + 1])}`
        )
    }
    parts.push(`L${round(x0 + to * dx)},${GRAPH_HEIGHT_PX}`, "Z")
    return parts.join("")
}

const buildChunks = (counts: Uint32Array, x0: number, dx: number) => {
    if (counts.length < 2) return []
    const ys = new Float32Array(counts.length)
    for (let i = 0; i < counts.length; i++) ys[i] = columnY(counts[i])
    const tangents = monotoneTangents(ys, dx)
    const last = counts.length - 1
    const paths: string[] = []
    for (let from = 0; from < last; from += CHUNK_BUCKETS) {
        paths.push(chunkPath(ys, tangents, from, Math.min(from + CHUNK_BUCKETS, last), x0, dx))
    }
    return paths
}

const useVisibleChunks = (
    scrollRef: RefObject<HTMLDivElement | null>,
    x0: number,
    chunkPx: number,
    chunkCount: number
) => {
    const [range, setRange] = useState<[number, number]>([0, -1])

    useEffect(() => {
        const container = scrollRef.current
        if (!container || chunkCount === 0 || chunkPx <= 0) return
        const update = () => {
            const half = container.clientWidth / 2
            const first = Math.max(
                0,
                Math.floor((container.scrollLeft - half - x0) / chunkPx) - CHUNK_MARGIN
            )
            const last = Math.min(
                chunkCount - 1,
                Math.floor((container.scrollLeft + half - x0) / chunkPx) + CHUNK_MARGIN
            )
            setRange((prev) => (prev[0] === first && prev[1] === last ? prev : [first, last]))
        }
        update()
        container.addEventListener("scroll", update, { passive: true })
        return () => container.removeEventListener("scroll", update)
    }, [scrollRef, x0, chunkPx, chunkCount])

    return range
}

type Props = {
    counts: Uint32Array
    x0: number
    dx: number
    width: number
    isLight: boolean
    scrollRef: RefObject<HTMLDivElement | null>
}

export const TimelineDensityGraph = ({ counts, x0, dx, width, isLight, scrollRef }: Props) => {
    const chunks = useMemo(() => buildChunks(counts, x0, dx), [counts, x0, dx])
    const [firstChunk, lastChunk] = useVisibleChunks(
        scrollRef,
        x0,
        CHUNK_BUCKETS * dx,
        chunks.length
    )

    if (chunks.length === 0) return null
    const visible: ReactNode[] = []
    const from = Math.min(firstChunk, chunks.length - 1)
    const to = Math.min(lastChunk, chunks.length - 1)
    for (let i = from; i <= to; i++) visible.push(<path key={i} d={chunks[i]} />)

    return (
        <svg
            className="pointer-events-none absolute left-0"
            style={{ top: -GRAPH_HEIGHT_PX, width, height: GRAPH_HEIGHT_PX }}
            aria-hidden="true"
        >
            <g
                fill={isLight ? "rgb(12,12,15)" : "rgb(180,180,192)"}
                opacity={isLight ? 0.22 : 0.35}
            >
                {visible}
            </g>
        </svg>
    )
}
